import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';

import type {
  BdPatientClinicalComplexityLevel,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BD_PATIENT_CLINICAL_COMPLEXITY_LEVEL_DISPLAY_NAMES,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

/**
 * PCCL – Patient Clinical Complexity Level Condition
 */

export const BD_PCCL_CONTENT_CONDITION_ID = 'pccl';
export const FilterConditionPcclPluginType: FilterConditionPluginSpecification<
  BdPatientClinicalComplexityLevel[],
  FilterConditionPcclDto,
  BdPcclContentCondition
> = {
  type: 'pccl',
  category: 'case',
  displayName: { de: 'PCCL Kriterium' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionPcclDto,
  ): FilterConditionPluginImpl<BdPatientClinicalComplexityLevel[]> {
    return new FilterConditionPcclPlugin(
        obj.pccl as BdPatientClinicalComplexityLevel[],
      );
  },
  fromCondition(
    jsonObj: BdPcclContentCondition,
  ): FilterConditionPluginImpl<BdPatientClinicalComplexityLevel[]> {
    return new FilterConditionPcclPlugin(
        (jsonObj.includes ?? []) as BdPatientClinicalComplexityLevel[],
      );
  },
  newInstance(): FilterConditionPluginImpl<BdPatientClinicalComplexityLevel[]> {
    return new FilterConditionPcclPlugin([]);
  },
};

export class FilterConditionPcclPlugin extends FilterConditionPluginImpl<
  BdPatientClinicalComplexityLevel[],
  FilterConditionPcclDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    BdPatientClinicalComplexityLevel[],
    FilterConditionPcclDto,
    BdPcclContentCondition
  > = FilterConditionPcclPluginType;

  constructor(private pccl: BdPatientClinicalComplexityLevel[]) {
    super(FilterConditionPcclPluginType.type);
  }

  clone(): FilterConditionPcclPlugin {
    return new FilterConditionPcclPlugin([...this.pccl]);
  }

  override equals(other: FilterConditionPcclPlugin): boolean {
    return super.equals(other) && BdArrayUtil.equals(this.pccl, other.pccl);
  }

  override validationToText(): string {
    if (this.pccl.length === 0) {
      return 'Mindestens ein PCCL Kriterium auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdPcclContentCondition(this.pccl, []);
  }

  toJSON(): FilterConditionPcclDto | null {
    return {
      type: this.type,
      pccl: this.pccl,
    };
  }

  toText(): string | null {
    return this.pccl.length === 1
      ? 'ein PCCL Kriterium'
      : `${this.pccl.length} PCCL Kriterien`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.pccl.map(
      (level) =>
        BD_PATIENT_CLINICAL_COMPLEXITY_LEVEL_DISPLAY_NAMES[level] ??
        String(level),
    );
    return { title, items };
  }

  toValue(): BdPatientClinicalComplexityLevel[] {
    return this.pccl;
  }
}

export class BdPcclContentCondition extends BdContentCondition<number> {
  constructor(
    includes: BdPatientClinicalComplexityLevel[],
    excludes: BdPatientClinicalComplexityLevel[],
  ) {
    super(BD_PCCL_CONTENT_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionPcclDto {
  type: string;
  pccl: number[];
}
